/**
 * XeAgents.ai - AI B2B Lead: the two lead sources behind one set of tabs.
 *
 * "Apollo.io" is the chat search that used to be the whole ProspectAI page. "Leads.cm" is the page that used to sit in
 * the sidebar on its own as Lead Lists, and it is still super admin only -- the tab is not drawn at all for anyone
 * else, which keeps a stale bookmark from putting it on screen during a demo.
 *
 * The active tab lives in ?tab= so the sidebar can link straight into either one.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { Building2, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApolloProspectSearch } from "@/pages/CampaignAIPage";
import AiB2bLeadsPage from "@/pages/AiB2bLeadsPage";

export const AI_B2B_LEAD_PATH = "/app/smart-prospecting";
export const AI_B2B_LEAD_APOLLO_TAB = "apollo";
export const AI_B2B_LEAD_LEADSCM_TAB = "leadscm";

export default function AiB2bLeadPage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const tabParam = new URLSearchParams(searchString).get("tab");

  const { data: user } = useQuery<{ isSuperAdmin?: boolean }>({ queryKey: ["/api/auth/me"] });
  // Same answer the sidebar reads, so the tab and the menu entry appear and disappear together.
  const { data: menu } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/prospect-leads/menu"],
    enabled: !!user?.isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const showLeadsCm = user?.isSuperAdmin === true && menu?.enabled === true;

  const wantsLeadsCm = tabParam === AI_B2B_LEAD_LEADSCM_TAB;
  const activeTab = wantsLeadsCm && showLeadsCm ? AI_B2B_LEAD_LEADSCM_TAB : AI_B2B_LEAD_APOLLO_TAB;

  // A link to the Leads.cm tab from someone who may not have it: once the answer is in and it is no, put the URL back
  // on the tab they are actually looking at rather than leaving ?tab= pointing at nothing.
  useEffect(() => {
    if (wantsLeadsCm && user && !showLeadsCm) {
      setLocation(AI_B2B_LEAD_PATH, { replace: true });
    }
  }, [wantsLeadsCm, user, showLeadsCm, setLocation]);

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(searchString);
    if (value === AI_B2B_LEAD_APOLLO_TAB) {
      params.delete("tab");
    } else {
      params.set("tab", value);
    }
    const query = params.toString();
    setLocation(query ? `${AI_B2B_LEAD_PATH}?${query}` : AI_B2B_LEAD_PATH, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="ai-b2b-lead-page">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("aiB2bLead.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("aiB2bLead.subtitle")}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList aria-label={t("aiB2bLead.tab.label")}>
          <TabsTrigger value={AI_B2B_LEAD_APOLLO_TAB} data-testid="tab-apollo">
            <Sparkles className="mr-2 h-4 w-4" />
            {t("aiB2bLead.tab.apollo")}
          </TabsTrigger>
          {showLeadsCm && (
            <TabsTrigger value={AI_B2B_LEAD_LEADSCM_TAB} data-testid="tab-leadscm">
              <Building2 className="mr-2 h-4 w-4" />
              {t("aiB2bLead.tab.leadsCm")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={AI_B2B_LEAD_APOLLO_TAB}>
          <ApolloProspectSearch embedded />
        </TabsContent>

        {showLeadsCm && (
          <TabsContent value={AI_B2B_LEAD_LEADSCM_TAB}>
            <AiB2bLeadsPage embedded />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
